import styled, { keyframes } from "styled-components";
import { useEffect, useState } from "react";
import { router } from "@inertiajs/react";

import { theme } from "./theme";
import { useT } from "./i18n";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 1; }
`;

// Full-screen frosted-charcoal veil shown during Inertia page switches. Opacity-transitioned so the
// fade-out is smooth; pointer-events off when hidden so it never blocks the UI.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 26, 26, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  opacity: ${(props) => (props.$show ? 1 : 0)};
  pointer-events: ${(props) => (props.$show ? "auto" : "none")};
  transition: opacity 0.25s ease;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  animation: ${fadeIn} 0.3s ease;
`;

const Ring = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 3px solid rgba(201, 168, 108, 0.25);
  border-top-color: ${theme.gold};
  box-shadow: 0 0 24px -4px rgba(201, 168, 108, 0.55);
  animation: ${spin} 0.9s linear infinite;
`;

const Brand = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.gold};
`;

const Caption = styled.div`
  font-size: 0.8rem;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.85);
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

// Small delay so instant navigations / quick-actions don't flash the veil.
const SHOW_DELAY_MS = 140;

const NavLoader = (props) => {
  const { t } = useT();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let timer;
    const onStart = router.on("start", (event) => {
      // Background refreshes (the 30s poll, post-action reloads, prev/next date) revisit the SAME
      // path. They must never flash the full-screen veil over content the FO is reading — only a
      // real navigation to a different page shows the loader.
      const visitUrl = event?.detail?.visit?.url;
      const samePath =
        visitUrl && typeof visitUrl.pathname === "string"
          ? visitUrl.pathname === window.location.pathname
          : false;
      if (samePath) {
        return;
      }
      timer = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    });
    const onFinish = router.on("finish", () => {
      clearTimeout(timer);
      setShow(false);
    });
    return () => {
      clearTimeout(timer);
      onStart();
      onFinish();
    };
  }, []);

  return (
    <Overlay $show={show} aria-hidden={!show} role="status" aria-live="polite">
      <Panel>
        <Ring />
        <Brand>{t("app_name")}</Brand>
        <Caption>{t("loading")}</Caption>
      </Panel>
    </Overlay>
  );
};

export default NavLoader;
